import React, { FC, ReactNode, useState } from 'react';
import { Button, Link } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { Form } from 'formik';
import { useTranslation, Trans } from 'react-i18next';

import {
  ActionContainer,
  FormikEffect,
  SubmitButton,
  ValidatedCheckbox,
  ValidatedTextField,
  VisibilityAdornment,
} from 'components';
import { Login } from 'Links';

import { ISignUpValues } from '../validations';

interface IProps extends WithStyles<typeof styles> {
  updateUsername: (username: string) => void;
  updatePassword: (password: string) => void;
  sidebar: ReactNode;
  working: boolean;
  hiddenEmail?: boolean;
}

const styles = (theme: Theme) =>
  createStyles({
    labelRoot: {
      marginTop: 12,
    },
    label: {
      lineHeight: '1rem',
      fontSize: theme.typography.pxToRem(13),
    },
  });

const SignUpForm: FC<IProps> = props => {
  const { classes, hiddenEmail, sidebar, working, updateUsername, updatePassword } = props;
  const [passwordVisible, setPasswordVisible] = useState(false);
  const { t } = useTranslation();

  function renderAcceptTermsText() {
    return (
      <Trans i18nKey="auth:acceptTerms">
        By checking this box and using EveryoneSocial you agree to our
        <Link
          color="primary"
          href="https://everyonesocial.com/user-terms-of-service/"
          target="_blank">
          {t('auth:userTerms')}
        </Link>
        and
        <Link color="primary" href="https://everyonesocial.com/privacy-policy/" target="_blank">
          {t('auth:privacyPolicy')}
        </Link>
      </Trans>
    );
  }

  function handleChange({ username, password }: ISignUpValues) {
    updateUsername(username.toLowerCase());
    updatePassword(password);
  }

  return (
    <Form>
      <FormikEffect onChange={handleChange} delay={100} />
      {sidebar}
      <ValidatedTextField hidden={hiddenEmail} name="username" i18nKey="user:email" />
      <ValidatedTextField
        name="password"
        type={passwordVisible ? 'text' : 'password'}
        i18nKey="auth:signUpPassword"
        InputProps={{
          endAdornment: (
            <VisibilityAdornment
              visible={passwordVisible}
              onToggle={() => setPasswordVisible(!passwordVisible)}
            />
          ),
        }}
      />
      <ValidatedCheckbox
        labelClasses={{ root: classes.labelRoot, label: classes.label }}
        name="acceptTerms"
        label={renderAcceptTermsText()}
      />

      <ActionContainer>
        <SubmitButton working={working} text={t('auth:signUpButton')} />
        <Button component={Login} fullWidth>
          {t('auth:signUpLogin')}
        </Button>
      </ActionContainer>
    </Form>
  );
};

export default withStyles(styles)(SignUpForm);
